import { Link, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Car, 
  Receipt, 
  Settings,
  ChevronLeft,
  ChevronRight,
  Users,
  DollarSign,
  Rocket,
  Wrench,
  HelpCircle,
  ArrowUpCircle,
  AlertTriangle,
  BarChart3,
  TrendingUp,
  Eye,
  Activity,
  ListChecks,
  Signal,
  Compass,
  Anchor,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { AnchorIcon } from "@/components/icons/AnchorIcon";
import { Button } from "@/components/ui/button";
import { useCompany } from "@/hooks/useCompany";
import { getDevPlanOverride } from "@/hooks/useDevPlanOverride";
import type { SubscriptionTier } from "@/hooks/useSubscription";

interface NavItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  href: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

interface AppSidebarProps {
  onNavigate?: () => void;
}

function getNavSections(tier: SubscriptionTier): NavSection[] {
  const sections: NavSection[] = [];

  // Dashboard — all plans
  sections.push({
    title: "Home",
    items: [
      { icon: LayoutDashboard, label: "Dashboard", href: "/app/dashboard" },
      { icon: Sparkles, label: "Captain AI", href: "/app/captain" },
    ],
  });

  const trackSection: NavSection = {
    title: "Track",
    items: [
      { icon: Car, label: "Vehicles", href: "/app/vehicles" },
      { icon: DollarSign, label: "Earnings", href: "/app/earnings" },
      { icon: Receipt, label: "Expenses", href: "/app/expenses" },
      { icon: Wrench, label: "Maintenance", href: "/app/maintenance" },
    ],
  };

  const learnSection: NavSection = {
    title: "Learn",
    items: [
      { icon: Rocket, label: "Your First Listing", href: "/app/launch-kit" },
    ],
  };

  if (tier === "free") {
    // Free plan: Learn & Orientation before Track
    sections.push(learnSection);
    sections.push({
      title: "Orientation",
      items: [
        { icon: ListChecks, label: "Setup Progress", href: "/app/orientation/setup" },
        { icon: Signal, label: "Basic Signals", href: "/app/orientation/signals" },
        { icon: Compass, label: "Next Steps", href: "/app/orientation/next" },
      ],
    });
    sections.push(trackSection);
  } else {
    sections.push(trackSection);
    sections.push(learnSection);
  }

  // Risk Prevention — Fleet only
  if (tier === "fleet") {
    sections.push({
      title: "Risk Prevention",
      items: [
        { icon: AlertTriangle, label: "What Could Go Wrong?", href: "/app/decisions/risk" },
        { icon: Activity, label: "Alerts & Exceptions", href: "/app/decisions/alerts" },
      ],
    });
  }

  // Focus — Scale & Fleet
  if (tier === "scale" || tier === "fleet") {
    sections.push({
      title: "Focus",
      items: [
        { icon: Eye, label: "Attention Required", href: "/app/decisions/attention" },
        { icon: Activity, label: "Fleet Overview", href: "/app/operations/overview" },
      ],
    });
  }

  // Decisions — Pro, Scale & Fleet
  if (tier === "pro" || tier === "scale" || tier === "fleet") {
    sections.push({
      title: "Decisions",
      items: [
        { icon: AlertTriangle, label: "Underperforming Vehicles", href: "/app/decisions/underperforming" },
        { icon: BarChart3, label: "Vehicle Comparisons", href: "/app/decisions/comparisons" },
        { icon: TrendingUp, label: "Profit Improvement", href: "/app/decisions/profit" },
      ],
    });
  }

  return sections;
}

const adminItems = [
  { icon: Users, label: "Team", href: "/app/team", ownerOnly: true },
  { icon: Settings, label: "Settings", href: "/app/settings", ownerOnly: false },
  { icon: HelpCircle, label: "Support", href: "/app/support", ownerOnly: false },
];

export const AppSidebar = ({ onNavigate }: AppSidebarProps) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { isOwner, company } = useCompany();
  const subscriptionTier = getDevPlanOverride() || (company?.subscription_tier as SubscriptionTier) || "free";

  const navSections = getNavSections(subscriptionTier);
  const visibleAdminItems = adminItems.filter(item => !item.ownerOnly || isOwner);
  const showUpgrade = subscriptionTier !== "scale" && subscriptionTier !== "fleet";

  const handleClick = () => {
    onNavigate?.();
  };

  return (
    <aside className={cn(
      "fixed left-0 top-0 z-40 h-screen bg-card border-r border-border transition-all duration-300",
      onNavigate ? "relative w-full h-full" : (collapsed ? "w-16" : "w-64")
    )}>
      <div className="flex h-full flex-col">
        {/* Logo — hide in mobile drawer since SheetContent has its own header area */}
        {!onNavigate && (
          <div className="flex h-16 items-center justify-between px-4 border-b border-border">
            {!collapsed && (
              <Link to="/app/dashboard" className="flex items-center gap-2">
                <AnchorIcon variant="blue" className="h-6 w-6" />
                <span className="font-bold text-foreground">HostCaptain</span>
              </Link>
            )}
            {collapsed && (
              <AnchorIcon variant="blue" className="h-6 w-6 mx-auto" />
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-6 overflow-y-auto">
          {navSections.map((section) => (
            <div key={section.title}>
              {(!collapsed || onNavigate) && (
                <p className="px-3 mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                  {section.title}
                </p>
              )}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + "/");
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={handleClick}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                        isActive 
                          ? "bg-primary text-primary-foreground" 
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      {(!collapsed || onNavigate) && <span>{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Upgrade CTA */}
          {showUpgrade && (
            <div>
              <Link
                to="/app/upgrade"
                onClick={handleClick}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  "text-primary hover:bg-primary/10"
                )}
              >
                <ArrowUpCircle className="h-5 w-5 flex-shrink-0" />
                {(!collapsed || onNavigate) && <span>Upgrade</span>}
              </Link>
            </div>
          )}

          {/* Admin Section */}
          {visibleAdminItems.length > 0 && (
            <div>
              {(!collapsed || onNavigate) && (
                <p className="px-3 mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                  Account
                </p>
              )}
              <div className="space-y-1">
                {visibleAdminItems.map((item) => {
                  const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + "/");
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={handleClick}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                        isActive 
                          ? "bg-primary text-primary-foreground" 
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      {(!collapsed || onNavigate) && <span>{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </nav>

        {/* Collapse Button — desktop only */}
        {!onNavigate && (
          <div className="p-4 border-t border-border">
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full justify-center"
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          </div>
        )}
      </div>
    </aside>
  );
};
